import { useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { Controller, type Control } from "react-hook-form";
import { OptionalMark, RichLabel } from "@patient-watch/forms";
import type { SumEqualsRule } from "@patient-watch/forms/pageValidation";

import { ImageCarousel, useCarouselSelection } from "@/features/blocks/components/ImageCarouselBlock";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { cn } from "@/lib/utils";
import { services as s } from "@/services";
import { useEntryValuesStore } from "../../inputs/hooks/useEntryValuesStore";
import CountStepper from "@/features/inputs/components/CountStepper";
import type { ImageCountRow } from "../utils/imageCounts";
import { useGroupSum } from "../hooks/useGroupSum";
import SumRemainingBar from "./SumRemainingBar";

interface Props {
    rule: SumEqualsRule;
    rows: ImageCountRow[];
    control: Control<Record<string, unknown>>;
    /** The form's own setter, so a count is part of the form as well as the saved entry values. */
    setFieldValue: (inputId: string, value: number | null) => void;
}

function toCount(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * A page of counts that must add up to a fixed number, each tied to a picture: one carousel, a row per
 * picture with plus and minus buttons, and a total that says how many are left or over. Tapping a row shows
 * its picture, and swiping to a picture highlights its row. The counts are the same inputs as ever, so the
 * saved answers do not change.
 */
const ImageCountsGroup = ({ rule, rows, control, setFieldValue }: Props) => {
    const copy = useFormPageCopy();
    const selection = useCarouselSelection();
    const { entryId } = useParams({ strict: false }) as { entryId?: string };
    const upsertEntryValue = useEntryValuesStore((state) => state.upsertEntryValue);

    // Every count gets a saved row up front, as InputSwitch does, so a blank is a blank and not a missing row.
    useEffect(() => {
        if (!entryId) return;
        for (const { input } of rows) {
            if (useEntryValuesStore.getState().getEntryValueByInputId(input.input_id)) continue;
            upsertEntryValue({
                entry_id: entryId,
                input_id: input.input_id,
                entry_value_id: s.uuidv4(),
                // A count starts at 0, the same as its form default.
                number_value: input.input_key === "count" ? 0 : null,
                text_value: null,
                text_array: null,
                json_value: null,
                object_id: null,
            });
        }
    }, [entryId, rows, upsertEntryValue]);

    const commit = (inputId: string, value: number | null) => {
        if (entryId) {
            const existing = useEntryValuesStore.getState().getEntryValueByInputId(inputId);
            upsertEntryValue({
                entry_id: entryId,
                input_id: inputId,
                entry_value_id: existing?.entry_value_id ?? s.uuidv4(),
                number_value: value,
            });
        }
        setFieldValue(inputId, value);
    };

    // Plus and typing stop at the total: a count can take at most what is left once the others are counted.
    const total = useGroupSum(rule.input_ids, rule.total, control);
    const remaining = Math.max(rule.total - total, 0);

    return (
        <div data-testid="image-counts" className="flex flex-col gap-4">
            <ImageCarousel
                compact
                lettered
                images={rows.map((row) => row.image)}
                label={rows[selection.selected]?.image.alt}
                {...selection}
            />

            <ul className="flex flex-col gap-2">
                {rows.map(({ input, image }, index) => {
                    const min = input.min_value ?? 0;
                    const max = input.max_value ?? rule.total;
                    const isShown = selection.selected === index;

                    return (
                        <Controller
                            key={input.input_id}
                            control={control}
                            name={input.input_id}
                            render={({ field, fieldState }) => {
                                const count = toCount(field.value);
                                const label = input.input_label ?? image.alt;
                                return (
                                    <li
                                        data-testid="image-count-row"
                                        data-shown={isShown ? "" : undefined}
                                        onClick={() => selection.api?.scrollTo(index)}
                                        className={cn(
                                            "flex items-center gap-3 border-2 rounded-lg p-2",
                                            isShown
                                                ? "border-indigo-600 dark:border-indigo-400"
                                                : "border-slate-200/80 dark:border-slate-700",
                                            fieldState.error &&
                                                "border-rose-300 bg-rose-50/80 dark:border-rose-800/60 dark:bg-rose-950/25"
                                        )}
                                    >
                                        <img
                                            src={image.src}
                                            alt=""
                                            className="rounded-md w-14 h-14 object-contain shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-lg leading-snug">
                                                <RichLabel>{label}</RichLabel>
                                                {input.is_optional && <OptionalMark />}
                                            </div>
                                            {fieldState.error ? (
                                                <p className="mt-1 text-rose-700 text-lg dark:text-rose-300">
                                                    {copy.countRequired}
                                                </p>
                                            ) : null}
                                        </div>
                                        <CountStepper
                                            inputRef={field.ref}
                                            name={field.name}
                                            value={count}
                                            min={min}
                                            max={Math.min(max, (count ?? 0) + remaining)}
                                            label={image.alt || label}
                                            onBlur={field.onBlur}
                                            onChange={(next) => commit(input.input_id, next)}
                                        />
                                    </li>
                                );
                            }}
                        />
                    );
                })}
            </ul>

            <SumRemainingBar inputIds={rule.input_ids} target={rule.total} control={control} />
        </div>
    );
};

export default ImageCountsGroup;
